<template>
  <div class="TaskDetails">
    <section
      v-for="section in detailSections"
      :key="section.key"
      class="TaskDetails__panel"
    >
      <div class="TaskDetails__panel-head">
        <span class="zp-overline">{{ section.title }}</span>
      </div>
      <div class="TaskDetails__rows">
        <div v-for="row in section.rows" :key="row.label">
          <span>{{ row.label }}</span>
          <strong :class="{ 'zp-tabular': row.mono }">{{ row.value }}</strong>
        </div>
      </div>
    </section>

    <section class="TaskDetails__code TaskDetails__code--wide">
      <div class="TaskDetails__panel-head">
        <span class="zp-overline">{{ $t('taskPayload') }}</span>
      </div>
      <div class="TaskDetails__code-grid">
        <div v-for="block in codeBlocks" :key="block.label">
          <div class="TaskDetails__code-title">
            <span>{{ block.label }}</span>
            <ZpIcon name="copy" :size="14" />
          </div>
          <pre>{{ block.value }}</pre>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss">
.TaskDetails {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--zp-space-sm);

  &__panel,
  &__code {
    min-width: 0;
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-md);
    background: var(--zp-bg-surface);
    overflow: hidden;
  }

  &__code--wide {
    grid-column: 1 / -1;
  }

  &__panel-head {
    padding: var(--zp-space-sm) var(--zp-space-md);
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    background: var(--zp-bg-page);
  }

  &__rows {
    padding: var(--zp-space-xs) var(--zp-space-md) var(--zp-space-sm);

    div {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: var(--zp-space-md);
      padding: var(--zp-space-xs) 0;
      border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
      font-size: var(--zp-text-xs);

      &:last-child {
        border-bottom: 0;
      }
    }

    span {
      color: var(--zp-text-secondary);
    }

    strong {
      min-width: 0;
      color: var(--zp-text-primary);
      font-weight: 650;
      text-align: right;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__code-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--zp-space-sm);
    padding: var(--zp-space-md);
  }

  &__code-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--zp-space-sm);
    padding: var(--zp-space-xs) var(--zp-space-sm);
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-bottom: 0;
    border-radius: var(--zp-radius-md) var(--zp-radius-md) 0 0;
    background: var(--zp-bg-page);
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-xs);
    font-weight: 700;
  }

  pre {
    min-height: 118px;
    max-height: 240px;
    margin: 0;
    padding: var(--zp-space-sm);
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: 0 0 var(--zp-radius-md) var(--zp-radius-md);
    background: oklch(16% 0.018 268);
    color: oklch(90% 0.018 268);
    font-family: var(--zp-font-mono);
    font-size: 12px;
    line-height: 1.5;
    overflow: auto;
    white-space: pre-wrap;
  }
}

@media (max-width: 1100px) {
  .TaskDetails {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    &__code-grid {
      grid-template-columns: 1fr;
    }
  }
}

@media (max-width: 700px) {
  .TaskDetails {
    grid-template-columns: 1fr;
  }
}
</style>

<script>
import ProjectMixin from '@/components/ProjectMixin';
import { APP_SHORT_TITLE, APP_TITLE } from '@/lib/constants';

export default {
  props: {
    item: Object,
    user: Object,
    projectId: Number,
    templateContext: Object,
  },

  mixins: [ProjectMixin],

  data() {
    return {
      template: null,
    };
  },

  watch: {
    async item() {
      if (this.item?.template_id !== this.template?.id) {
        await this.loadData();
      }
    },
  },

  computed: {
    currentTemplate() {
      return this.templateContext || this.template;
    },

    detailSections() {
      return [
        {
          key: 'identity',
          title: this.$t('taskIdentity'),
          rows: [
            { label: this.$t('taskId'), value: this.formatId(this.item.id), mono: true },
            { label: this.$t('status'), value: this.item.status || '—' },
            { label: this.$t('application'), value: this.appTitle },
            { label: this.$t('user'), value: this.user?.name || this.item.user_name || '—' },
          ],
        },
        {
          key: 'source',
          title: this.$t('templateSource'),
          rows: [
            { label: this.$t('template'), value: this.currentTemplate?.name || '—' },
            { label: this.$t('templateId'), value: this.formatId(this.item.template_id), mono: true },
            { label: this.$t('playbook'), value: this.item.playbook || this.currentTemplate?.playbook || '—' },
            { label: this.$t('branch'), value: this.item.get_branch || this.currentTemplate?.git_branch || '—' },
          ],
        },
        {
          key: 'runtime',
          title: this.$t('executionEnvironmentSingular'),
          rows: [
            { label: this.$t('inventory'), value: this.item.inventory || this.item.inventory_id || '—' },
            { label: this.$t('environment'), value: this.compactJson(this.item.environment) || '—' },
            { label: 'Limit', value: this.paramsList('limit') || '—' },
            { label: this.$t('debug'), value: this.item.params?.debug ? 'Yes' : 'No' },
            { label: this.$t('dryRun'), value: this.item.params?.dry_run ? 'Yes' : 'No' },
            { label: this.$t('diff'), value: this.item.params?.diff ? 'Yes' : 'No' },
          ],
        },
        {
          key: 'timing',
          title: this.$t('timing'),
          rows: [
            { label: this.$t('created'), value: this.formatDateTime(this.item.created), mono: true },
            { label: this.$t('started'), value: this.formatDateTime(this.item.start), mono: true },
            { label: this.$t('end'), value: this.formatDateTime(this.item.end), mono: true },
            { label: this.$t('duration'), value: this.formatDuration(this.item.start, this.item.end), mono: true },
          ],
        },
      ];
    },

    codeBlocks() {
      return [
        {
          label: this.$t('taskParameters'),
          value: this.prettyJson(this.item.params || {}),
        },
        {
          label: this.$t('extraVariables'),
          value: this.prettyJson(this.item.environment || {}),
        },
        {
          label: this.$t('templateComposerCliArgs'),
          value: this.prettyJson(this.item.arguments || []),
        },
      ];
    },

    appTitle() {
      const app = this.currentTemplate?.app;
      return APP_SHORT_TITLE[app] || APP_TITLE[app] || app || '—';
    },
  },

  async created() {
    await this.loadData();
  },

  methods: {
    async loadData() {
      if (this.templateContext || this.item?.template_id == null) {
        return;
      }
      this.template = await this.loadProjectResource('templates', this.item.template_id);
    },

    formatId(value) {
      return value == null ? '—' : `#${String(value).padStart(4, '0')}`;
    },

    formatDateTime(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString();
    },

    formatDuration(start, end) {
      if (!start) return '—';
      const startTime = new Date(start).getTime();
      const endTime = end ? new Date(end).getTime() : Date.now();
      if (Number.isNaN(startTime) || Number.isNaN(endTime)) return '—';
      const totalSeconds = Math.max(0, Math.floor((endTime - startTime) / 1000));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },

    prettyJson(value) {
      if (value == null || value === '') return '{}';
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return `${value}`;
      }
    },

    compactJson(value) {
      if (!value || value === '{}') return '';
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        const count = Object.keys(parsed || {}).length;
        return count > 0 ? `${count} ${this.$t('items')}` : '';
      } catch (e) {
        return `${value}`;
      }
    },

    paramsList(key) {
      const value = this.item.params?.[key];
      return Array.isArray(value) ? value.join(', ') : value;
    },
  },
};
</script>
